import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Car, FileText, Users, Shield, CheckCircle, Phone, Menu, X, ChevronRight, Upload, Trash2, Award, TrendingUp, Mail, MessageCircle } from "lucide-react";
import LocationSection from "@/components/LocationSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NOI_ITEMS = [
  "Oferim solutii personalizate, adaptate specificului fiecarui incident in parte.",
  "Ne ocupam noi de tot ce inseamna acte, de la prima notificare catre asigurator pana cand vezi banii in cont sau masina reparata.",
  "Participam activ la evaluarea tehnica alaturi de inspectorul asiguratorului pentru a valida toate avariile identificate — etapa cheie pentru despagubirea corecta.",
  "Efectuam personal reconstatarea in centrul nostru pentru a ne asigura ca nu exista avarii ascunse care nu s-au vazut la constatarea initiala, fara a genera costuri suplimentare pentru tine.",
  "Negociem direct cu asiguratorul si nu ne oprim pana cand nu obtinem suma corecta pentru reparatia ta.",
  "Identificam si implementam cea mai avantajoasa strategie pentru tine, optimizand atat resursele financiare, cat si timpul de solutionare."
];

const ALTII_ITEMS = [
  "Se ocupa doar cu reparatia — daca vrei strict banii, esti trimis in alta parte",
  "Te trimit singur la constatare, fara niciun suport",
  "Incearca sa obtina o oferta pe baza unei constatari facute in avantajul asiguratorului",
  "Reconstatari si devize suplimentare pe care le platesti din buzunarul tau",
  "Accepta prima oferta a asiguratorului fara negociere",
  "Te descurci pe cont propriu cu actele si termenele"
];

const ComparisonList = ({ items, variant }) => {
  const isGood = variant === "good";
  return (
    <ul className="space-y-4">
      {items.map((text) => (
        <li key={text.slice(0, 30)} className="flex items-start gap-3">
          <div className={`w-5 h-5 mt-0.5 rounded-full flex items-center justify-center flex-shrink-0 ${isGood ? "bg-emerald-500" : "bg-red-400"}`}>
            {isGood ? <CheckCircle className="w-3.5 h-3.5 text-white" /> : <X className="w-3.5 h-3.5 text-white" />}
          </div>
          <span className={`text-sm leading-relaxed ${isGood ? "text-gray-700" : "text-gray-600"}`}>{text}</span>
        </li>
      ))}
    </ul>
  );
};

// Gallery of successful cases with compensation amounts
const REZULTATE_GALLERY = [
  { image: "https://customer-assets.emergentagent.com/job_9b2de055-6bf4-4bba-ab53-bcb8274a6458/artifacts/czohm46b_168032.png", suma: 168032 },
  { image: "https://customer-assets.emergentagent.com/job_autopro-affiliate/artifacts/im99vg00_51963.JPG", suma: 51963 },
  { image: "https://customer-assets.emergentagent.com/job_autopro-affiliate/artifacts/4ww2bi49_45634.JPG", suma: 45634 },
  { image: "https://customer-assets.emergentagent.com/job_9b2de055-6bf4-4bba-ab53-bcb8274a6458/artifacts/c4hibxtk_37702.png", suma: 37702 },
  { image: "https://customer-assets.emergentagent.com/job_9b2de055-6bf4-4bba-ab53-bcb8274a6458/artifacts/8zpmd682_27445.png", suma: 27445 },
  { image: "https://customer-assets.emergentagent.com/job_9b2de055-6bf4-4bba-ab53-bcb8274a6458/artifacts/4vj26gaf_17354.png", suma: 17354 },
  { image: "https://customer-assets.emergentagent.com/job_autopro-affiliate/artifacts/hwegihlz_13976.JPG", suma: 13976 },
  { image: "https://customer-assets.emergentagent.com/job_autopro-affiliate/artifacts/detm1sjk_10925.JPG", suma: 10925 },
  { image: "https://customer-assets.emergentagent.com/job_autopro-affiliate/artifacts/i8hk5yuq_5500.jpeg", suma: 5500 },
  { image: "https://customer-assets.emergentagent.com/job_autopro-affiliate/artifacts/9gpr02b6_3759.JPG", suma: 3759 }
];

const Header = () => {
  const [mobileMenu, setMobileMenu] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-lg text-gray-900 leading-tight">AUTO PRO</span>
              <span className="text-xs text-emerald-600 font-medium -mt-1">DAUNE</span>
            </div>
          </Link>
          
          <nav className="hidden md:flex items-center gap-6">
            <a href="#servicii" className="text-gray-600 hover:text-emerald-600 transition-colors">Servicii</a>
            <a href="#rezultate" className="text-gray-600 hover:text-emerald-600 transition-colors">Rezultate</a>
            <a href="#cum-functioneaza" className="text-gray-600 hover:text-emerald-600 transition-colors">Cum functioneaza</a>
            <a href="#locatie" className="text-gray-600 hover:text-emerald-600 transition-colors">Locatie</a>
            <Link to="/verificare-dosar" className="text-gray-600 hover:text-emerald-600 transition-colors">Verifica Dosar</Link>
            <a href="#formular" className="text-gray-600 hover:text-emerald-600 transition-colors">Contact</a>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a href="tel:0725399235" className="flex items-center gap-1.5 text-gray-600 hover:text-emerald-600 transition-colors" data-testid="header-phone-btn">
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">0725 399 235</span>
            </a>
            <Link to="/affiliate/login" className="px-4 py-2 text-gray-600 hover:text-emerald-600 transition-colors" data-testid="header-affiliate-btn">
              Portal Afiliați
            </Link>
            <a href="#formular" className="px-6 py-2.5 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 transition-colors hover-lift" data-testid="header-cta-btn">
              Solicită Ajutor
            </a>
          </div>

          <button className="md:hidden p-2" onClick={() => setMobileMenu(!mobileMenu)} data-testid="mobile-menu-btn">
            {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileMenu && (
        <div className="md:hidden bg-white border-t border-gray-100 py-4">
          <nav className="flex flex-col gap-2 px-4">
            <a href="#servicii" className="py-2 text-gray-600">Servicii</a>
            <a href="#rezultate" className="py-2 text-gray-600">Rezultate</a>
            <a href="#cum-functioneaza" className="py-2 text-gray-600">Cum functioneaza</a>
            <a href="#locatie" className="py-2 text-gray-600">Locatie</a>
            <Link to="/verificare-dosar" className="py-2 text-gray-600">Verifica Dosar</Link>
            <a href="tel:0725399235" className="py-2 text-gray-600 flex items-center gap-2">
              <Phone className="w-4 h-4" />0725 399 235
            </a>
            <Link to="/affiliate/login" className="py-2 text-gray-600">Portal Afiliați</Link>
            <a href="#formular" className="mt-2 px-6 py-2.5 bg-emerald-500 text-white rounded-full text-center">
              Solicită Ajutor
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

const Footer = () => {
  const [open, setOpen] = useState(null);
  const toggle = (section) => setOpen(open === section ? null : section);

  return (
  <footer className="bg-gray-900 text-gray-400 py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-4 gap-8 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-white leading-tight">AUTO PRO</span>
              <span className="text-xs text-emerald-400 -mt-1">DAUNE</span>
            </div>
          </div>
          <div className="text-xs text-gray-500 space-y-1 mt-3">
            <p className="font-medium text-gray-400">SC AUTO PRO DAUNE & ASIGURARI SRL</p>
            <p>CUI: 49492739</p>
            <p>Nr. Reg. Com.: J40/1769/2024</p>
            <p>Bucuresti, Romania</p>
          </div>
        </div>
        <div>
          <button onClick={() => toggle("contact")} className="flex items-center justify-between w-full text-left" data-testid="footer-toggle-contact">
            <h4 className="text-white font-semibold">Contact</h4>
            <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform md:hidden ${open === "contact" ? "rotate-90" : ""}`} />
          </button>
          <div className={`space-y-3 mt-4 overflow-hidden transition-all duration-300 ${open === "contact" ? "max-h-40" : "max-h-0 md:max-h-40"}`}>
            <a href="mailto:office@autoprodaune.ro" className="flex items-center gap-2 text-sm hover:text-emerald-400 transition-colors" data-testid="footer-email">
              <Mail className="w-4 h-4" />office@autoprodaune.ro
            </a>
            <a href="tel:0725399235" className="flex items-center gap-2 text-sm hover:text-emerald-400 transition-colors" data-testid="footer-phone">
              <Phone className="w-4 h-4" />0725 399 235
            </a>
            <a href="https://wa.me/40725399235" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm hover:text-emerald-400 transition-colors" data-testid="footer-whatsapp">
              <MessageCircle className="w-4 h-4" />WhatsApp
            </a>
          </div>
        </div>
        <div>
          <button onClick={() => toggle("juridic")} className="flex items-center justify-between w-full text-left" data-testid="footer-toggle-juridic">
            <h4 className="text-white font-semibold">Juridic</h4>
            <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform md:hidden ${open === "juridic" ? "rotate-90" : ""}`} />
          </button>
          <div className={`space-y-3 mt-4 overflow-hidden transition-all duration-300 ${open === "juridic" ? "max-h-48" : "max-h-0 md:max-h-48"}`}>
            <Link to="/termeni-conditii" className="block text-sm hover:text-white transition-colors" data-testid="footer-terms">Termeni si Conditii</Link>
            <Link to="/politica-confidentialitate" className="block text-sm hover:text-white transition-colors" data-testid="footer-privacy">Politica de Confidentialitate</Link>
            <Link to="/politica-cookies" className="block text-sm hover:text-white transition-colors" data-testid="footer-cookies">Politica de Cookies</Link>
            <Link to="/affiliate/login" className="block text-sm hover:text-white transition-colors">Portal Afiliati</Link>
          </div>
        </div>
        <div>
          <button onClick={() => toggle("autoritati")} className="flex items-center justify-between w-full text-left" data-testid="footer-toggle-autoritati">
            <h4 className="text-white font-semibold">Autoritati</h4>
            <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform md:hidden ${open === "autoritati" ? "rotate-90" : ""}`} />
          </button>
          <div className={`space-y-3 mt-4 overflow-hidden transition-all duration-300 ${open === "autoritati" ? "max-h-40" : "max-h-0 md:max-h-40"}`}>
            <a href="https://www.anpc.ro" target="_blank" rel="noopener noreferrer" className="block text-sm hover:text-white transition-colors" data-testid="footer-anpc">ANPC - anpc.ro</a>
            <a href="https://www.anpc.ro/ce-este-sal/" target="_blank" rel="noopener noreferrer" className="block text-sm hover:text-white transition-colors" data-testid="footer-sal">SAL - Solutionarea Alternativa a Litigiilor</a>
            <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="block text-sm hover:text-white transition-colors" data-testid="footer-sol">SOL - Solutionarea Online a Litigiilor</a>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-800 pt-6">
        <p className="text-xs text-gray-500 mb-4 leading-relaxed">AutoPro Daune ofera consultanta si asistenta in procesul de despagubire. Decizia finala de plata si suma aprobata depind de asigurator si de conditiile politei RCA/CASCO.</p>
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm">&copy; 2026 SC AUTO PRO DAUNE & ASIGURARI SRL. Toate drepturile rezervate.</p>
          <div className="flex gap-6 text-sm">
            <Link to="/politica-confidentialitate" className="hover:text-white transition-colors">Confidentialitate</Link>
            <Link to="/termeni-conditii" className="hover:text-white transition-colors">Termeni</Link>
            <Link to="/politica-cookies" className="hover:text-white transition-colors">Cookies</Link>
          </div>
        </div>
      </div>
    </div>
  </footer>
  );
};

const ResultsGallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  
  const formatSum = (suma) => {
    return suma.toLocaleString('ro-RO') + ' lei';
  };

  const totalRecuperat = REZULTATE_GALLERY.reduce((acc, r) => acc + r.suma, 0);

  return (
    <section id="rezultate" className="py-20 bg-gray-900" data-testid="results-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-medium mb-6">
            <TrendingUp className="w-4 h-4" />Rezultate reale
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Despăgubiri obținute</h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">Cazuri reale gestionate de echipa noastră</p>
          <div className="inline-block bg-emerald-500/10 border border-emerald-500/30 rounded-2xl px-8 py-4">
            <p className="text-emerald-400 text-sm mb-1">Total recuperat în aceste cazuri</p>
            <p className="text-4xl font-bold text-white">{formatSum(totalRecuperat)}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {REZULTATE_GALLERY.map((item, i) => (
            <div 
              key={`result-${item.suma}`} 
              className="group relative overflow-hidden rounded-xl cursor-pointer"
              onClick={() => setSelectedImage(item)}
              data-testid={`result-card-${i}`}
            >
              <div className="aspect-square">
                <img 
                  src={item.image} 
                  alt={`Caz ${i + 1}`} 
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-emerald-400 text-xs font-medium mb-1">Despăgubire obținută</p>
                <p className="text-white text-xl font-bold">{formatSum(item.suma)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 text-lg"
            >
              Închide ✕
            </button>
            <img
              src={selectedImage.image}
              alt="Caz detaliu"
              className="max-w-full max-h-[80vh] rounded-lg object-contain"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6 rounded-b-lg">
              <p className="text-emerald-400 text-sm font-medium mb-1">Despăgubire obținută</p>
              <p className="text-white text-3xl font-bold">{formatSum(selectedImage.suma)}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const SERVICII_OPTIONS = [
  { key: "despagubire_cash", label: "Despagubire cash" },
  { key: "reparatie_service", label: "Reparatie Service" },
  { key: "masina_schimb", label: "Masina la schimb" },
  { key: "tractare", label: "Tractare" }
];

const ClientForm = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [numarDosar, setNumarDosar] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [files, setFiles] = useState([]);
  const [acordGdpr, setAcordGdpr] = useState(false);
  const [serviciiSolicitate, setServiciiSolicitate] = useState([]);
  const [formData, setFormData] = useState({
    nume_prenume: "",
    telefon: "",
    email: "",
    oras: "",
    tip_dauna: "",
    detalii: "",
    cod_recomandare: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleServiciu = (key) => {
    setServiciiSolicitate(prev => prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]);
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles([...files, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) data.append(key, formData[key]);
      });
      data.append("honeypot", honeypot);
      serviciiSolicitate.forEach(s => data.append("servicii_solicitate", s));
      files.forEach(file => data.append("documente", file));

      const res = await axios.post(`${API}/leads`, data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setSuccess(true);
      setNumarDosar(res.data.numar_dosar);
      toast.success("Cererea a fost trimisa cu succes!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "A aparut o eroare.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8 animate-fade-in">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-emerald-500" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Multumim!</h3>
        <p className="text-gray-600 mb-2">Te vom contacta in curand.</p>
        {numarDosar && <p className="text-sm text-gray-500">Numar dosar: <span className="font-mono font-medium">{numarDosar}</span></p>}
        <button 
          onClick={() => { setSuccess(false); setFormData({ nume_prenume: "", telefon: "", email: "", oras: "", tip_dauna: "", detalii: "", cod_recomandare: "" }); setFiles([]); setServiciiSolicitate([]); }}
          className="mt-6 px-6 py-2.5 bg-emerald-500 text-white rounded-full hover:bg-emerald-600"
          data-testid="form-new-btn"
        >
          Trimite Alta Cerere
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Nume și prenume *</label>
        <input type="text" name="nume_prenume" value={formData.nume_prenume} onChange={handleChange} required className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-emerald-500" placeholder="Ion Popescu" data-testid="form-nume" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Telefon *</label>
        <div className="relative">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input type="tel" name="telefon" value={formData.telefon} onChange={handleChange} required pattern="^07[0-9]{8}$" title="Numar de telefon valid (ex: 07XX XXX XXX)" className="w-full h-12 pl-12 pr-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-emerald-500" placeholder="07XX XXX XXX" data-testid="form-telefon" />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
        <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-emerald-500" placeholder="email@exemplu.ro" data-testid="form-email" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Oras *</label>
        <select name="oras" value={formData.oras} onChange={handleChange} required className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-emerald-500" data-testid="form-oras">
          <option value="">Selectează</option>
          <option value="București">București</option>
          <option value="Ilfov">Ilfov</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Tip dauna *</label>
        <select name="tip_dauna" value={formData.tip_dauna} onChange={handleChange} required className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-emerald-500" data-testid="form-tip-dauna">
          <option value="">Selectează</option>
          <option value="RCA">RCA</option>
          <option value="CASCO">CASCO</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Servicii solicitate</label>
        <div className="grid grid-cols-2 gap-2">
          {SERVICII_OPTIONS.map(s => (
            <label key={s.key} className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${serviciiSolicitate.includes(s.key) ? "bg-emerald-50 border-emerald-300" : "bg-gray-50 border-gray-200 hover:border-emerald-200"}`} data-testid={`service-${s.key}`}>
              <input type="checkbox" checked={serviciiSolicitate.includes(s.key)} onChange={() => toggleServiciu(s.key)} className="w-4 h-4 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500" />
              <span className="text-sm text-gray-700">{s.label}</span>
            </label>
          ))}
        </div>
        {serviciiSolicitate.includes("masina_schimb") && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-sm text-blue-800 font-medium mb-1">Masina la schimb</p>
            <p className="text-xs text-blue-600">Pe perioada reparatiei, iti punem la dispozitie o masina de inlocuire pentru a nu ramane fara mijloc de transport.</p>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Detalii eveniment *</label>
        <textarea name="detalii" value={formData.detalii} onChange={handleChange} required rows={3} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-emerald-500 resize-none" placeholder="Descrie pe scurt ce s-a intamplat..." data-testid="form-detalii" />
      </div>

      <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
        <label className="block text-sm font-medium text-emerald-700 mb-1">Cod de recomandare <span className="text-emerald-500 text-xs">(opțional)</span></label>
        <input type="text" name="cod_recomandare" value={formData.cod_recomandare} onChange={handleChange} className="w-full h-12 px-4 rounded-xl border border-emerald-200 bg-white focus:border-emerald-500 font-mono uppercase" placeholder="EX: ABC123" data-testid="form-cod" />
      </div>

      <input type="text" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} style={{ position: 'absolute', left: '-9999px', opacity: 0 }} tabIndex={-1} autoComplete="off" />

      <label className="flex items-start gap-3 cursor-pointer" data-testid="gdpr-checkbox-label">
        <input type="checkbox" checked={acordGdpr} onChange={(e) => setAcordGdpr(e.target.checked)} className="mt-1 w-4 h-4 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500" data-testid="gdpr-checkbox" />
        <span className="text-xs text-gray-500 leading-relaxed">
          Sunt de acord cu prelucrarea datelor cu caracter personal conform{" "}
          <Link to="/politica-confidentialitate" className="text-emerald-600 hover:underline" target="_blank">Politicii de Confidentialitate</Link>.
        </span>
      </label>

      <button type="submit" disabled={loading || !acordGdpr} className="w-full h-14 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 disabled:bg-emerald-300 hover-lift flex items-center justify-center gap-2 text-lg font-medium" data-testid="form-submit">
        {loading ? (<><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Se trimite...</>) : (<>Trimite Cererea <ChevronRight className="w-5 h-5" /></>)}
      </button>

      <p className="text-xs text-gray-400 text-center">AutoPro Daune ofera consultanta si asistenta. Decizia finala de plata si suma aprobata depind de asigurator si de conditiile politei RCA/CASCO.</p>
    </form>
  );
};

const LandingPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-24 pb-16 bg-gradient-hero" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />Reprezentare profesională
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Recuperează-ți <span className="text-emerald-500">despăgubirea</span> corectă
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Ne ocupam de dosarul tau de dauna auto si te reprezentam in relatia cu asiguratorul.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#formular" className="px-8 py-4 bg-emerald-500 text-white rounded-full text-lg font-medium hover:bg-emerald-600 hover-lift shadow-lg shadow-emerald-200 flex items-center justify-center gap-2" data-testid="hero-cta">
                  Solicită Ajutor <ChevronRight className="w-5 h-5" />
                </a>
                <a href="#rezultate" className="px-8 py-4 bg-gray-900 text-white rounded-full text-lg font-medium hover:bg-gray-800 flex items-center justify-center gap-2">
                  Vezi Rezultate <TrendingUp className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="relative animate-fade-in">
              <div className="absolute -top-4 -left-4 w-72 h-72 bg-emerald-200 rounded-full blur-3xl opacity-30" />
              <img src="https://customer-assets.emergentagent.com/job_9b2de055-6bf4-4bba-ab53-bcb8274a6458/artifacts/h0sql74r_Incasare%20despagubire.png" alt="Încasare despăgubire AutoPro Daune" className="relative rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]" />
            </div>
          </div>
        </div>
      </section>

      <section id="servicii" className="py-20 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Ce facem pentru tine</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">De ce sa alegi AutoPro Daune in locul altor firme?</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-8" data-testid="comparison-us">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-emerald-700">AutoPro Daune</h3>
              </div>
              <ComparisonList items={NOI_ITEMS} variant="good" />
            </div>
            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8" data-testid="comparison-others">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-400 rounded-xl flex items-center justify-center">
                  <X className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-red-600">Alte firme</h3>
              </div>
              <ComparisonList items={ALTII_ITEMS} variant="bad" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-gray-50 border-y border-gray-100" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-medium text-gray-400 uppercase tracking-widest mb-10">Asiguratori cu care lucram</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 items-center">
            {[
              { name: "Allianz Tiriac", logo: "https://customer-assets.emergentagent.com/job_9b2de055-6bf4-4bba-ab53-bcb8274a6458/artifacts/rtxjliqt_Screenshot%202026-04-09%20051353.png" },
              { name: "Asirom", logo: "https://customer-assets.emergentagent.com/job_9b2de055-6bf4-4bba-ab53-bcb8274a6458/artifacts/akzlhhl8_Screenshot%202026-04-09%20051214.png" },
              { name: "Axeria", logo: "https://customer-assets.emergentagent.com/job_9b2de055-6bf4-4bba-ab53-bcb8274a6458/artifacts/zmk09iuk_Screenshot%202026-04-09%20051222.png" },
              { name: "Generali", logo: "https://customer-assets.emergentagent.com/job_9b2de055-6bf4-4bba-ab53-bcb8274a6458/artifacts/v75s5y7i_Screenshot%202026-04-09%20051233.png" },
              { name: "Grawe", logo: "https://customer-assets.emergentagent.com/job_9b2de055-6bf4-4bba-ab53-bcb8274a6458/artifacts/4pwam52j_Screenshot%202026-04-09%20051238.png" },
              { name: "Groupama", logo: "https://customer-assets.emergentagent.com/job_9b2de055-6bf4-4bba-ab53-bcb8274a6458/artifacts/xmtevvon_Screenshot%202026-04-09%20051249.png" },
              { name: "Hellas Direct", logo: "https://customer-assets.emergentagent.com/job_9b2de055-6bf4-4bba-ab53-bcb8274a6458/artifacts/3977uugn_Screenshot%202026-04-09%20051253.png" },
              { name: "Omniasig", logo: "https://customer-assets.emergentagent.com/job_9b2de055-6bf4-4bba-ab53-bcb8274a6458/artifacts/57l5t63w_Screenshot%202026-04-09%20051259.png" },
            ].map((item) => (
              <div key={item.name} className="flex items-center justify-center h-20 px-3 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all" data-testid={`partner-${item.name.toLowerCase().replace(' ', '-')}`}>
                <img src={item.logo} alt={item.name} className="max-h-14 max-w-full object-contain" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <ResultsGallery />

      <section id="cum-functioneaza" className="py-20 bg-gray-50" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Cum funcționează</h2>
            <p className="text-xl text-gray-600">Procesul este simplu și rapid</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { num: "1", title: "Completezi formularul", desc: "Ne spui ce s-a întâmplat" },
              { num: "2", title: "Te contactăm", desc: "Discutăm detaliile" },
              { num: "3", title: "Preluăm dosarul", desc: "Ne ocupăm de tot" },
              { num: "4", title: "Primești banii", desc: "Despăgubirea corectă" }
            ].map((item) => (
              <div key={item.num} className="text-center">
                <div className="w-14 h-14 bg-emerald-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">{item.num}</div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-900" data-testid="check-status-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">Verifica statusul dosarului</h2>
          <p className="text-gray-400 mb-6 text-sm">Ai un dosar deschis? Verifica statusul acestuia pe pagina dedicata.</p>
          <Link to="/verificare-dosar" className="inline-flex items-center gap-2 px-8 py-3 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 font-medium" data-testid="go-to-status-page">
            Verifica Status Dosar <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <LocationSection variant="landing" />

      <section id="formular" className="py-20 bg-gray-50" data-testid="form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Hai să vorbim</h2>
              <p className="text-xl text-gray-600 mb-8">Completează formularul sau contactează-ne direct.</p>
              <div className="space-y-4">
                <a href="tel:0725399235" className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-emerald-50 transition-colors group" data-testid="contact-phone-btn">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <Phone className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Sună-ne acum</p>
                    <p className="text-sm text-emerald-600 font-medium">0725 399 235</p>
                  </div>
                </a>
                <a href="https://wa.me/40725399235" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-emerald-50 transition-colors group" data-testid="contact-whatsapp-btn">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <MessageCircle className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Scrie-ne pe WhatsApp</p>
                    <p className="text-sm text-emerald-600 font-medium">Răspundem rapid</p>
                  </div>
                </a>
                <a href="mailto:office@autoprodaune.ro" className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-emerald-50 transition-colors group" data-testid="contact-email-btn">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <Mail className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Trimite email</p>
                    <p className="text-sm text-emerald-600 font-medium">office@autoprodaune.ro</p>
                  </div>
                </a>
              </div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
              <ClientForm />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
